import { MapContext } from 'contexts/MapContext';
import * as Styled from './styles';
import * as RiIcons from 'react-icons/ri';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLayers } from 'hooks/useLayers';

import VectorLayer from 'ol/layer/Vector';
import TileLayer from 'ol/layer/Tile';
import { TileWMS } from 'ol/source';
import { ToolBox } from 'screens/Dashboard/components/SideBar/ToolBox';
import { BaseSelect } from 'components/BaseSelect';

function getName(str: string) {
  const key = 'p3m:';
  const index = str?.indexOf(key);
  return index !== -1 ? 'p3m:' + str?.slice(index + key.length) : '';
}

function getLayerId(layer: any): string {
  const props = layer?.getProperties?.() ?? {};

  if (props.layertitle) return String(props.layertitle);
  if (props.uuid) return String(props.uuid);

  return '';
}

function removeSuffix(value: string) {
  return value.replace(/\.(kml|geojson|zip|shp)$/i, '');
}

export function IntersectLayersMenu() {
  const { getLayerAttritubeByName } = useLayers();

  const {
    map,
    intersectFirstLayer,
    intersectSecondLayer,
    isIntersecting,
    handleSetIntersectFirstLayer,
    handleSetIntersectSecondLayer,
    handleApplyIntersectLayers,
  } = useContext(MapContext);

  const [layersVersion, setLayersVersion] = useState(0);

  useEffect(() => {
    if (!map) return;

    const layers = map.getLayers();

    const onChange = () => {
      setLayersVersion(v => v + 1);
    };

    layers.on('add', onChange);
    layers.on('remove', onChange);

    return () => {
      layers.un('add', onChange);
      layers.un('remove', onChange);
    };
  }, [map]);

  function getVectorLayerLabel(layer: any) {
    const id = getLayerId(layer);
    let meta = id;

    try {
      meta = getLayerAttritubeByName(getName(id))?.layertitle || id;
    } catch (error) {
      meta = id;
    }

    return removeSuffix(meta);
  }

  function getWmsLayerLabel(layer: any) {
    const source = layer?.getSource?.();
    const params = source instanceof TileWMS ? source.getParams() || {} : {};
    const layerName = String(params.LAYERS || '');
    const rawId = String(layer.get('uuid') || layer.get('layertitle') || layerName);

    try {
      const metadata =
        getLayerAttritubeByName(layerName)?.layertitle ||
        getLayerAttritubeByName(getName(rawId))?.layertitle;

      if (metadata) return `${removeSuffix(metadata)} (camada do sistema)`;
    } catch (error) {}

    const fallbackName = removeSuffix(layerName.split(':').pop() || rawId);
    return `${fallbackName} (camada do sistema)`;
  }

  const layersForIntersect = useMemo(() => {
    if (!map) return [];

    const mapLayers = map.getLayers().getArray();

    const vectors = mapLayers
      .filter((layer: any) => {
        if (!(layer instanceof VectorLayer)) return false;

        const props = layer?.getProperties?.() ?? {};
        return !!(props.layertitle || props.uuid);
      })
      .map((layer: any) => {
        const id = getLayerId(layer);
        return {
          key: `vector::${id}`,
          value: `vector::${id}`,
          label: getVectorLayerLabel(layer),
          type: 'Arquivo',
        };
      });

    const wmsActive = mapLayers
      .filter((layer: any) => {
        if (!(layer instanceof TileLayer)) return false;
        const source = layer.getSource?.();
        if (!(source instanceof TileWMS)) return false;
        const props = layer?.getProperties?.() ?? {};
        return !!(props.uuid || props.layertitle);
      })
      .map((layer: any) => {
        const id = String(layer.get('uuid') || layer.get('layertitle'));
        return {
          key: `wms::${id}`,
          value: `wms::${id}`,
          label: getWmsLayerLabel(layer),
          type: 'Sistema',
        };
      });

    return [...vectors, ...wmsActive];
  }, [map, layersVersion, getLayerAttritubeByName]);

  const selectedFirstId = intersectFirstLayer ? `${intersectFirstLayer.type}::${intersectFirstLayer.id}` : '';

  const selectedSecondId = intersectSecondLayer ? `${intersectSecondLayer.type}::${intersectSecondLayer.id}` : '';

  const isFirstSystemLayer = intersectFirstLayer?.type === 'wms';
  const isSecondSystemLayer = intersectSecondLayer?.type === 'wms';

  const layersForFirst = useMemo(
    () =>
      layersForIntersect.filter((layer) => {
        if (layer.value === selectedSecondId) return false;
        if (isSecondSystemLayer && layer.value.startsWith('wms::')) return false;
        return true;
      }),
    [isSecondSystemLayer, layersForIntersect, selectedSecondId]
  );

  const layersForSecond = useMemo(
    () =>
      layersForIntersect.filter((layer) => {
        if (layer.value === selectedFirstId) return false;
        if (isFirstSystemLayer && layer.value.startsWith('wms::')) return false;
        return true;
      }),
    [isFirstSystemLayer, layersForIntersect, selectedFirstId]
  );

  useEffect(() => {
    if (!selectedFirstId) {
      if (selectedSecondId) handleSetIntersectSecondLayer('disable');
      return;
    }

    if (selectedSecondId && selectedSecondId === selectedFirstId) {
      handleSetIntersectSecondLayer('disable');
    }
  }, [selectedFirstId, selectedSecondId, handleSetIntersectSecondLayer]);

  useEffect(() => {
    if (isFirstSystemLayer && isSecondSystemLayer) {
      handleSetIntersectSecondLayer('disable');
    }
  }, [handleSetIntersectSecondLayer, isFirstSystemLayer, isSecondSystemLayer]);

  return (
    <ToolBox
      header={
        <>
          <RiIcons.RiScissorsCutLine />
          <span>Interseção de Camadas</span>
        </>
      }
    >
      <BaseSelect
        label={'Camada 1'}
        value={selectedFirstId || 'disable'}
        onChange={handleSetIntersectFirstLayer}
        placeholder='Selecione a camada'
        options={layersForFirst}
      />

      <BaseSelect
        label={'Camada 2'}
        value={selectedSecondId || 'disable'}
        onChange={handleSetIntersectSecondLayer}
        placeholder='Selecione a camada'
        options={layersForSecond}
      />

      <Styled.ApplyButton
        onClick={handleApplyIntersectLayers}
        disabled={isIntersecting}
      >
        {isIntersecting ? 'Aplicando interseção...' : 'Aplicar interseção'}
      </Styled.ApplyButton>
    </ToolBox>
  );
}